export type CodexEvent =
    | CodexThreadStartedEvent
    | CodexTurnStartedEvent
    | CodexTurnCompletedEvent
    | CodexTurnFailedEvent
    | CodexItemStartedEvent
    | CodexItemUpdatedEvent
    | CodexItemCompletedEvent
    | CodexErrorEvent
    | CodexWebSearchEvent
    | CodexRawJsonEvent

export interface CodexThreadStartedEvent {
    type: "thread.started"
    thread_id: string
    session_id?: string
    cwd?: string
    model?: string
    additional_directories?: string[]
}

export interface CodexTurnStartedEvent {
    type: "turn.started"
}

export interface CodexTurnCompletedEvent {
    type: "turn.completed"
    usage: CodexUsage
}

export interface CodexTurnFailedEvent {
    type: "turn.failed"
    error: { message?: string; [key: string]: unknown }
}

export interface CodexItemStartedEvent {
    type: "item.started"
    item: CodexItem
}

export interface CodexItemUpdatedEvent {
    type: "item.updated"
    item: CodexItem
}

export interface CodexItemCompletedEvent {
    type: "item.completed"
    item: CodexItem
}

export interface CodexErrorEvent {
    type: "error"
    message: string
}

export interface CodexWebSearchEvent {
    type: "web_search"
    id?: string
    query?: string
    action?: {
        type?: string
        query?: string
        queries?: string[]
        [key: string]: unknown
    }
    [key: string]: unknown
}

export interface CodexRawJsonEvent {
    type: "raw_json"
    original_type?: string
    raw: Record<string, unknown>
}

export interface CodexUsage {
    input_tokens: number
    cached_input_tokens: number
    output_tokens: number
}

export type CodexItem =
    | CodexReasoningItem
    | CodexAgentMessageItem
    | CodexCommandExecutionItem
    | CodexOrchestrationItem
    | CodexCustomToolCallItem
    | CodexFileChangeItem
    | CodexMcpToolCallItem
    | CodexTodoListItem
    | CodexErrorItem
    | CodexWebSearchItem
    | CodexUnsupportedItem

export interface CodexReasoningItem {
    id: string
    type: "reasoning"
    text: string
}

export interface CodexAgentMessageItem {
    id: string
    type: "agent_message"
    text: string
}

export interface CodexCommandExecutionItem {
    id: string
    type: "command_execution"
    toolName?: string
    input?: string
    command: string
    aggregated_output: string
    exit_code: number | null
    status: "in_progress" | "completed" | "failed" | "declined" | string
}

export interface CodexToolMedia {
    type: "image"
    url: string
    detail?: string
}

export interface CodexOrchestrationItem {
    id: string
    type: "orchestration"
    input: string
    output: string
    nested_output?: string
    media?: CodexToolMedia[]
    session_id?: number
    duration_ms?: number
    exit_code: number | null
    status: "in_progress" | "completed" | "failed" | string
}

export interface CodexCustomToolCallItem {
    id: string
    type: "custom_tool_call"
    toolName: string
    input: unknown
    output: string
    media?: CodexToolMedia[]
    status: "in_progress" | "completed" | "failed" | string
}

export interface CodexFileChangeItem {
    id: string
    type: "file_change"
    changes: CodexFileChange[]
    status: "in_progress" | "completed" | "failed" | "declined" | string
}

export interface CodexFileChange {
    path: string
    kind: "add" | "delete" | "update" | string
    diff?: string
}

export interface CodexMcpToolCallItem {
    id: string
    type: "mcp_tool_call"
    server: string
    tool: string
    arguments: unknown
    result?: {
        content: unknown[]
        structured_content: unknown
    }
    error?: {
        message: string
    }
    status: "in_progress" | "completed" | "failed" | string
}

export interface CodexTodoListItem {
    id: string
    type: "todo_list"
    items: Array<{ text: string; completed: boolean }>
}

export interface CodexErrorItem {
    id: string
    type: "error"
    message: string
}

export interface CodexWebSearchItem {
    id: string
    type: "web_search"
    query?: string
    action?: {
        type?: string
        query?: string
        queries?: string[]
        [key: string]: unknown
    }
    [key: string]: unknown
}

export interface CodexUnsupportedItem {
    id: string
    type: "unsupported"
    original_type?: string
    raw: Record<string, unknown>
}

const CODEX_TOP_TYPES = new Set<string>([
    "thread.started",
    "turn.started",
    "turn.completed",
    "turn.failed",
    "item.started",
    "item.updated",
    "item.completed",
    "error",
    "web_search",
])

const CODEX_ITEM_TYPES = new Set<string>([
    "reasoning",
    "agent_message",
    "command_execution",
    "orchestration",
    "custom_tool_call",
    "file_change",
    "mcp_tool_call",
    "web_search",
    "todo_list",
    "error",
])

export function parseCodexEvent(json: unknown): CodexEvent | null {
    if (!json || typeof json !== "object" || Array.isArray(json)) return null

    const obj = json as Record<string, unknown>
    const type = typeof obj.type === "string" ? obj.type : undefined
    if (!type) return null

    if ((type === "item.started" || type === "item.updated" || type === "item.completed") && obj.item && typeof obj.item === "object") {
        return {
            ...obj,
            item: parseCodexItem(obj.item),
        } as unknown as CodexEvent
    }

    if (CODEX_TOP_TYPES.has(type)) {
        return obj as unknown as CodexEvent
    }

    return {
        type: "raw_json",
        original_type: type,
        raw: obj,
    }
}

function parseCodexItem(item: object): CodexItem {
    const obj = item as Record<string, unknown>
    const type = typeof obj.type === "string" ? obj.type : undefined

    if (type && CODEX_ITEM_TYPES.has(type)) {
        return obj as unknown as CodexItem
    }

    return {
        id: typeof obj.id === "string" ? obj.id : "",
        type: "unsupported",
        original_type: type,
        raw: obj,
    }
}

type NativeCodexLineType = "session_meta" | "turn_context" | "event_msg" | "response_item"

interface NativeCodexRolloutLine {
    type: NativeCodexLineType
    timestamp?: string
    payload: Record<string, unknown>
}

interface IndexedNativeCodexMessage {
    index: number
    message: unknown
    native: NativeCodexRolloutLine | null
}

interface NativeFunctionOutput {
    rawOutput: string
    output: string
    exitCode: number | null
    media: CodexToolMedia[]
    sessionId?: number
    durationMs?: number
}

interface NativeCodexNormalizationContext {
    callOutputs: Map<string, NativeFunctionOutput>
    hasAssistantResponseMessage: boolean
    finalUsage?: CodexUsage
}

export interface CodexProviderNormalizationStats {
    nativeCount: number
    normalizedCount: number
    skippedCount: number
    typeCounts: Record<string, number>
}

export interface NormalizedCodexProviderEvents {
    events: CodexEvent[]
    sourceIndices: number[]
    stats?: CodexProviderNormalizationStats
}

const NATIVE_CODEX_LINE_TYPES = new Set<string>(["session_meta", "turn_context", "event_msg", "response_item"])
const NATIVE_CODEX_ITEM_COMPLETED_MIRRORS = new Set<string>([
    "Reasoning",
    "AgentMessage",
    "UserMessage",
    "CommandExecution",
    "McpToolCall",
    "FileChange",
    "Extension",
    "ContextCompaction",
    "CollabAgentToolCall",
    "ImageView",
])
const NORMALIZED_CODEX_ITEM_TYPES = new Set<string>([
    "reasoning",
    "agent_message",
    "command_execution",
    "orchestration",
    "custom_tool_call",
    "file_change",
    "mcp_tool_call",
    "web_search",
    "todo_list",
    "error",
    "unsupported",
])

export function normalizeCodexProviderEvents(messages: readonly unknown[]): NormalizedCodexProviderEvents {
    const indexed = messages.map((message, index): IndexedNativeCodexMessage => ({ index, message, native: nativeCodexRolloutLineFromUnknown(message) }))
    const nativeCount = indexed.filter((entry) => entry.native).length

    if (nativeCount === 0) {
        const events: CodexEvent[] = []
        const sourceIndices: number[] = []
        for (const entry of indexed) {
            const event = parseCodexProviderEvent(entry.message)
            if (!event) continue
            events.push(event)
            sourceIndices.push(entry.index)
        }
        return { events, sourceIndices }
    }

    const typeCounts: Record<string, number> = {}
    for (const entry of indexed) {
        if (!entry.native) continue
        const kind = nativeCodexLineKind(entry.native)
        typeCounts[kind] = (typeCounts[kind] ?? 0) + 1
    }

    const context = collectNativeCodexContext(indexed)
    const ordered = nativeCount === indexed.length ? [...indexed].sort(compareNativeCodexMessages) : indexed
    const events: CodexEvent[] = []
    const sourceIndices: number[] = []
    let skippedCount = 0

    for (const entry of ordered) {
        if (!entry.native) {
            const parsed = parseCodexProviderEvent(entry.message)
            if (parsed) {
                events.push(parsed)
                sourceIndices.push(entry.index)
            } else skippedCount += 1
            continue
        }
        const event = codexEventFromNativeLine(entry.native, entry.index, context)
        if (event) {
            events.push(event)
            sourceIndices.push(entry.index)
        } else skippedCount += 1
    }

    return {
        events,
        sourceIndices,
        stats: {
            nativeCount,
            normalizedCount: events.length,
            skippedCount,
            typeCounts,
        },
    }
}

export function parseCodexProviderEvent(message: unknown): CodexEvent | null {
    const native = nativeCodexRolloutLineFromUnknown(message)
    if (native) return codexEventFromNativeLine(native, 0, collectNativeCodexContext([{ index: 0, message, native }]))
    if (isAlreadyNormalizedCodexEvent(message)) return message
    return parseCodexEvent(message)
}

function isAlreadyNormalizedCodexEvent(message: unknown): message is CodexEvent {
    const record = recordValue(message)
    if (!record) return false
    const type = stringValue(record?.type)
    if (!type) return false
    if (type === "raw_json") return recordValue(record.raw) !== null
    if (type === "item.started" || type === "item.updated" || type === "item.completed") {
        const item = recordValue(record.item)
        const itemType = stringValue(item?.type)
        return itemType !== undefined && NORMALIZED_CODEX_ITEM_TYPES.has(itemType)
    }
    return (
        type === "thread.started" || type === "turn.started" || type === "turn.completed" || type === "turn.failed" || type === "error" || type === "web_search"
    )
}

function collectNativeCodexContext(indexed: IndexedNativeCodexMessage[]): NativeCodexNormalizationContext {
    const callOutputs = new Map<string, NativeFunctionOutput>()
    let hasAssistantResponseMessage = false
    let finalUsage: CodexUsage | undefined

    for (const entry of indexed) {
        const native = entry.native
        if (!native) continue

        if (native.type === "event_msg" && stringValue(native.payload.type) === "token_count") {
            finalUsage = codexUsageFromTokenCount(native.payload) ?? finalUsage
            continue
        }

        if (native.type !== "response_item") continue
        const itemType = stringValue(native.payload.type)

        if (itemType === "message" && stringValue(native.payload.role) === "assistant" && textFromContent(native.payload.content).trim()) {
            hasAssistantResponseMessage = true
            continue
        }

        if (itemType !== "function_call_output" && itemType !== "custom_tool_call_output") continue
        const callId = stringValue(native.payload.call_id)
        if (!callId) continue
        callOutputs.set(callId, parseNativeFunctionOutput(native.payload.output))
    }

    return { callOutputs, hasAssistantResponseMessage, ...(finalUsage ? { finalUsage } : {}) }
}

function codexEventFromNativeLine(line: NativeCodexRolloutLine, index: number, context: NativeCodexNormalizationContext): CodexEvent | null {
    if (line.type === "session_meta") {
        const threadId =
            stringValue(line.payload.id) ?? stringValue(line.payload.session_id) ?? stringValue(line.payload.sessionId) ?? `native-session-${index}`
        const cwd = stringValue(line.payload.cwd)
        const model = stringValue(line.payload.model) ?? stringValue(line.payload.model_provider)
        const event: CodexThreadStartedEvent = {
            type: "thread.started",
            thread_id: threadId,
            session_id: threadId,
            ...(cwd ? { cwd } : {}),
            ...(model ? { model } : {}),
        }
        return event
    }

    if (line.type === "turn_context") return null

    if (line.type === "event_msg") {
        const eventType = stringValue(line.payload.type)
        if (eventType === "turn_started" || eventType === "task_started") {
            const event: CodexTurnStartedEvent = { type: "turn.started" }
            return event
        }
        if (eventType === "turn_complete" || eventType === "task_complete") {
            const rawError = line.payload.error
            if (rawError !== undefined && rawError !== null) {
                const error = recordValue(rawError)
                const message = stringValue(error?.message) ?? stringValue(rawError) ?? "Turn failed"
                const event: CodexTurnFailedEvent = {
                    type: "turn.failed",
                    error: { ...error, message },
                }
                return event
            }
            const event: CodexTurnCompletedEvent = {
                type: "turn.completed",
                usage: context.finalUsage ?? { input_tokens: 0, cached_input_tokens: 0, output_tokens: 0 },
            }
            return event
        }
        if (eventType === "agent_message" && !context.hasAssistantResponseMessage) {
            return codexAgentMessageEvent(line.payload, index)
        }
        if (eventType === "item_completed") {
            const item = recordValue(line.payload.item)
            const itemType = stringValue(item?.type)
            if (itemType === "SubAgentActivity") return nativeRawJsonEvent(line, "item_completed:SubAgentActivity")
            if (itemType && NATIVE_CODEX_ITEM_COMPLETED_MIRRORS.has(itemType)) return null
            return nativeRawJsonEvent(line, itemType ? `item_completed:${itemType}` : "item_completed")
        }
        if (eventType === "user_message" || eventType === "agent_message" || eventType === "token_count" || eventType === "patch_apply_end") return null
        return nativeRawJsonEvent(line, eventType ?? "event_msg")
    }

    const itemType = stringValue(line.payload.type)
    if (itemType === "message") {
        if (stringValue(line.payload.role) !== "assistant") return null
        return codexAgentMessageEvent(line.payload, index)
    }
    if (itemType === "function_call") return codexCommandExecutionEvent(line.payload, index, context, "function_call")
    if (itemType === "custom_tool_call") {
        const toolName = stringValue(line.payload.name)
        if (toolName === "exec") return codexOrchestrationEvent(line.payload, index, context)
        if (toolName !== "apply_patch") return codexCustomToolCallEvent(line.payload, index, context)
        return codexCommandExecutionEvent(line.payload, index, context, "custom_tool_call")
    }
    if (itemType === "reasoning") {
        const text = textFromSummary(line.payload.summary)
        if (!text.trim()) return null
        const item: CodexReasoningItem = {
            id: stringValue(line.payload.id) ?? `native-reasoning-${index}`,
            type: "reasoning",
            text,
        }
        return { type: "item.completed", item }
    }
    if (itemType === "function_call_output" || itemType === "custom_tool_call_output") return null
    if (itemType) return nativeRawJsonEvent(line, itemType)
    return nativeRawJsonEvent(line, "response_item")
}

function codexAgentMessageEvent(payload: Record<string, unknown>, index: number): CodexEvent | null {
    const text = textFromContent(payload.content) || stringValue(payload.message) || stringValue(payload.text) || ""
    if (!text.trim()) return null
    const item: CodexAgentMessageItem = {
        id: stringValue(payload.id) ?? `native-agent-message-${index}`,
        type: "agent_message",
        text,
    }
    return { type: "item.completed", item }
}

function codexCommandExecutionEvent(
    payload: Record<string, unknown>,
    index: number,
    context: NativeCodexNormalizationContext,
    fallbackType: string
): CodexEvent {
    const callId = stringValue(payload.call_id)
    const correlated = callId ? context.callOutputs.get(callId) : undefined
    const name = stringValue(payload.name) ?? fallbackType
    const item: CodexCommandExecutionItem = {
        id: stringValue(payload.id) ?? callId ?? `native-command-${index}`,
        type: "command_execution",
        toolName: name,
        input: stringValue(payload.input) ?? stringValue(payload.arguments),
        command: commandFromNativeToolCall(payload, fallbackType),
        aggregated_output: correlated?.output ?? "",
        exit_code: correlated?.exitCode ?? null,
        status:
            correlated?.exitCode !== undefined && correlated.exitCode !== null && correlated.exitCode !== 0
                ? "failed"
                : (stringValue(payload.status) ?? "completed"),
    }
    return { type: "item.completed", item }
}

function codexOrchestrationEvent(payload: Record<string, unknown>, index: number, context: NativeCodexNormalizationContext): CodexEvent {
    const callId = stringValue(payload.call_id)
    const correlated = callId ? context.callOutputs.get(callId) : undefined
    const item: CodexOrchestrationItem = {
        id: stringValue(payload.id) ?? callId ?? `native-orchestration-${index}`,
        type: "orchestration",
        input: stringValue(payload.input) ?? "",
        output: correlated?.rawOutput ?? "",
        ...(correlated ? { nested_output: correlated.output } : {}),
        ...(correlated?.media.length ? { media: correlated.media } : {}),
        ...(correlated?.sessionId !== undefined ? { session_id: correlated.sessionId } : {}),
        ...(correlated?.durationMs !== undefined ? { duration_ms: correlated.durationMs } : {}),
        exit_code: correlated?.exitCode ?? null,
        status:
            correlated?.sessionId !== undefined && correlated.exitCode === null
                ? "in_progress"
                : correlated?.exitCode !== undefined && correlated.exitCode !== null && correlated.exitCode !== 0
                  ? "failed"
                  : (stringValue(payload.status) ?? "completed"),
    }
    return { type: "item.completed", item }
}

function codexCustomToolCallEvent(payload: Record<string, unknown>, index: number, context: NativeCodexNormalizationContext): CodexEvent {
    const callId = stringValue(payload.call_id)
    const correlated = callId ? context.callOutputs.get(callId) : undefined
    const item: CodexCustomToolCallItem = {
        id: stringValue(payload.id) ?? callId ?? `native-custom-tool-${index}`,
        type: "custom_tool_call",
        toolName: stringValue(payload.name) ?? "Custom tool",
        input: payload.input,
        output: correlated?.output ?? "",
        ...(correlated?.media.length ? { media: correlated.media } : {}),
        status:
            correlated?.exitCode !== undefined && correlated.exitCode !== null && correlated.exitCode !== 0
                ? "failed"
                : (stringValue(payload.status) ?? "completed"),
    }
    return { type: "item.completed", item }
}

function commandFromNativeToolCall(payload: Record<string, unknown>, fallbackType: string): string {
    const name = stringValue(payload.name) ?? fallbackType
    const argumentsRecord = jsonRecordFromString(payload.arguments)
    const cmd = stringValue(argumentsRecord?.cmd) ?? stringValue(argumentsRecord?.command)
    if (cmd) return cmd
    if (name === "write_stdin") {
        const sessionId = stringValue(argumentsRecord?.session_id) ?? numberString(argumentsRecord?.session_id)
        return sessionId ? `write_stdin ${sessionId}` : name
    }
    return name
}

function parseNativeFunctionOutput(value: unknown): NativeFunctionOutput {
    const outputString = stringValue(value) ?? textFromContentBlocks(value)
    const media = mediaFromContentBlocks(value)
    const envelope = scriptOutputEnvelope(outputString)
    const semanticSource = envelope?.body ?? outputString
    const parsed = jsonRecordFromString(semanticSource.trim())
    if (!parsed) {
        return {
            rawOutput: outputString,
            output: semanticSource,
            exitCode: envelope?.exitCode ?? exitCodeFromOutput(outputString),
            media,
            ...(envelope?.durationMs !== undefined ? { durationMs: envelope.durationMs } : {}),
        }
    }

    const stdout = stringValue(parsed.stdout)
    const stderr = stringValue(parsed.stderr)
    const output = stringValue(parsed.output)
    const streams = [stdout, stderr].filter((part): part is string => Boolean(part)).join("\n")
    const text = streams ? streams : output !== undefined ? output : semanticSource
    const sessionId = numberValue(parsed.session_id) ?? numberValue(parsed.sessionId)
    const nestedExitCode = numberValue(parsed.exit_code) ?? numberValue(parsed.exitCode)
    const exitCode = nestedExitCode ?? (sessionId !== undefined ? null : (envelope?.exitCode ?? exitCodeFromOutput(text)))
    return {
        rawOutput: outputString,
        output: text,
        exitCode,
        media,
        ...(sessionId !== undefined ? { sessionId } : {}),
        ...(envelope?.durationMs !== undefined ? { durationMs: envelope.durationMs } : {}),
    }
}

function scriptOutputEnvelope(output: string): { body: string; exitCode: number; durationMs?: number } | null {
    const match = output.match(/^Script (completed|failed)\r?\n(?:Wall time ([\d.]+) seconds\r?\n)?Output:(?:\r?\n)?/)
    if (!match) return null
    const seconds = Number(match[2])
    const body = output.slice(match[0].length).replace(/^\r?\n/, "")
    return {
        body,
        exitCode: match[1] === "completed" ? 0 : 1,
        ...(Number.isFinite(seconds) ? { durationMs: seconds * 1000 } : {}),
    }
}

function mediaFromContentBlocks(value: unknown): CodexToolMedia[] {
    if (!Array.isArray(value)) return []
    const media: CodexToolMedia[] = []
    for (const block of value) {
        const record = recordValue(block)
        const type = stringValue(record?.type)
        if (type !== "input_image" && type !== "image" && type !== "image_url") continue
        const url = stringValue(record?.image_url)
        if (!url?.startsWith("data:image/")) continue
        const detail = stringValue(record?.detail)
        media.push({ type: "image", url, ...(detail ? { detail } : {}) })
    }
    return media
}

function textFromContentBlocks(value: unknown): string {
    if (!Array.isArray(value)) return ""
    return value
        .map((block) => {
            const record = recordValue(block)
            const type = stringValue(record?.type)
            if (type !== "output_text" && type !== "text" && type !== "input_text") return ""
            return stringValue(record?.text) ?? ""
        })
        .filter((text) => text.length > 0)
        .join("\n")
}

function codexUsageFromTokenCount(payload: Record<string, unknown>): CodexUsage | null {
    const info = recordValue(payload.info)
    const usage = recordValue(info?.total_token_usage) ?? recordValue(info?.last_token_usage) ?? recordValue(payload.total_token_usage)
    if (!usage) return null
    return {
        input_tokens: numberValue(usage.input_tokens) ?? 0,
        cached_input_tokens: numberValue(usage.cached_input_tokens) ?? 0,
        output_tokens: numberValue(usage.output_tokens) ?? 0,
    }
}

function nativeCodexRolloutLineFromUnknown(value: unknown): NativeCodexRolloutLine | null {
    const record = recordValue(value)
    const type = stringValue(record?.type)
    if (!type || !isNativeCodexLineType(type)) return null
    const payload = recordValue(record?.payload)
    if (!payload) return null
    return {
        type,
        payload,
        ...(typeof record?.timestamp === "string" ? { timestamp: record.timestamp } : {}),
    }
}

function isNativeCodexLineType(type: string): type is NativeCodexLineType {
    return NATIVE_CODEX_LINE_TYPES.has(type)
}

function compareNativeCodexMessages(a: IndexedNativeCodexMessage, b: IndexedNativeCodexMessage): number {
    const aTime = a.native?.timestamp
    const bTime = b.native?.timestamp
    if (aTime && bTime && aTime !== bTime) return aTime.localeCompare(bTime)
    if (aTime && !bTime) return -1
    if (!aTime && bTime) return 1
    return a.index - b.index
}

function nativeCodexLineKind(line: NativeCodexRolloutLine): string {
    const payloadType = stringValue(line.payload.type)
    return payloadType ? `${line.type}.${payloadType}` : line.type
}

function nativeRawJsonEvent(line: NativeCodexRolloutLine, originalType: string): CodexRawJsonEvent {
    const raw: Record<string, unknown> = {
        type: line.type,
        payload: line.payload,
    }
    if (line.timestamp) raw.timestamp = line.timestamp
    return { type: "raw_json", original_type: originalType, raw }
}

function textFromContent(value: unknown): string {
    if (typeof value === "string") return value
    if (!Array.isArray(value)) return ""
    return value
        .map((block) => {
            const record = recordValue(block)
            const type = stringValue(record?.type)
            if (type !== "output_text" && type !== "text" && type !== "input_text") return ""
            return stringValue(record?.text) ?? ""
        })
        .join("")
}

function textFromSummary(value: unknown): string {
    if (typeof value === "string") return value
    if (!Array.isArray(value)) return ""
    return value
        .map((block) => {
            const record = recordValue(block)
            return stringValue(record?.text) ?? ""
        })
        .join("")
}

function jsonRecordFromString(value: unknown): Record<string, unknown> | null {
    const text = stringValue(value)
    if (!text) return null
    try {
        return recordValue(JSON.parse(text))
    } catch {
        return null
    }
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined
}

function numberValue(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

function numberString(value: unknown): string | undefined {
    const number = numberValue(value)
    return number === undefined ? undefined : String(number)
}

function exitCodeFromOutput(output: string): number | null {
    if (output.startsWith("Script completed\n") || output === "Script completed") return 0
    if (output.startsWith("Script failed\n") || output === "Script failed") return 1
    const match = output.match(/(?:Process exited with code|Exit code:)\s*(-?\d+)/)
    if (!match?.[1]) return null
    const parsed = Number(match[1])
    return Number.isFinite(parsed) ? parsed : null
}
